import type { AnswerValue, FormQuestion, FormSection, VialProtocols } from "@allergy123/contracts/clinical";
import { Field, Select } from "@allergy123/ui";
import { useId } from "react";

export type Answers = Record<string, AnswerValue>;

/** A question shows when it has no dependency or the dependency's answer equals the declared value. */
export function visible(q: FormQuestion, answers: Answers): boolean {
  if (q.hidden) return false;
  if (!q.dependsOn) return true;
  const v = answers[q.dependsOn.questionKey];
  if (Array.isArray(v)) return v.includes(String(q.dependsOn.equals));
  return v === q.dependsOn.equals;
}

const control = "min-h-10 rounded-sm border border-rule-strong bg-card px-3 py-2 text-body font-normal text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus";

/** Renders one question by its declared value type (C-16, C-46: single-select where only one answer makes sense). */
export function QuestionField({ q, value, onChange, vials, disabled = false }: { q: FormQuestion; value: AnswerValue | undefined; onChange: (value: AnswerValue | null) => void; vials?: VialProtocols; disabled?: boolean }) {
  const id = useId();
  const label = q.required ? `${q.label} *` : q.label;
  switch (q.valueType) {
    case "boolean":
      return (
        <fieldset className="flex flex-col gap-2" disabled={disabled}>
          <legend className="text-small font-medium text-ink">{label}</legend>
          <div className="flex gap-4">
            {[
              ["yes", "Yes", true],
              ["no", "No", false],
            ].map(([k, text, v]) => (
              <label key={String(k)} className="flex items-center gap-2 text-body">
                <input type="radio" name={id} checked={value === v} onChange={() => onChange(v as boolean)} className="h-4 w-4 accent-[var(--cta-bg)]" />
                {text as string}
              </label>
            ))}
          </div>
          {q.helpText && <p className="text-small text-ink-secondary">{q.helpText}</p>}
        </fieldset>
      );
    case "integer":
    case "decimal":
      return <Field label={label} type="number" inputMode={q.valueType === "integer" ? "numeric" : "decimal"} step={q.valueType === "integer" ? 1 : "any"} min={q.min ?? undefined} max={q.max ?? undefined} value={typeof value === "number" ? value : ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} hint={q.helpText ?? undefined} disabled={disabled} />;
    case "currency_cents":
      return <Field label={`${label} (dollars)`} type="number" inputMode="decimal" step="0.01" min={0} value={typeof value === "number" ? (value / 100).toFixed(2) : ""} onChange={(e) => onChange(e.target.value === "" ? null : Math.round(Number(e.target.value) * 100))} hint={q.helpText ?? undefined} disabled={disabled} />;
    case "date":
      return <Field label={label} type="date" value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value || null)} hint={q.helpText ?? undefined} disabled={disabled} />;
    case "datetime":
      return <Field label={label} type="datetime-local" value={typeof value === "string" ? value.slice(0, 16) : ""} onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : null)} hint={q.helpText ?? undefined} disabled={disabled} />;
    case "single_choice":
    case "season_rank":
    case "exposure": {
      const options = (q.options ?? []).map((o) => ({ value: o.value, label: o.label }));
      const current = value === undefined || value === null ? "" : String(value);
      return (
        <Select
          label={label}
          options={options}
          placeholder="Choose…"
          value={current}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "") return onChange(null);
            onChange(q.valueType === "season_rank" ? Number(v) : v);
          }}
          hint={q.helpText ?? undefined}
          disabled={disabled}
        />
      );
    }
    case "multi_choice": {
      const selected = Array.isArray(value) ? value : [];
      return (
        <fieldset className="flex flex-col gap-2" disabled={disabled}>
          <legend className="text-small font-medium text-ink">{label}</legend>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {(q.options ?? []).map((o) => (
              <label key={o.value} className="flex items-center gap-2 text-body">
                <input type="checkbox" checked={selected.includes(o.value)} onChange={(e) => onChange(e.target.checked ? [...selected, o.value] : selected.filter((x) => x !== o.value))} className="h-4 w-4 accent-[var(--cta-bg)]" />
                {o.label}
              </label>
            ))}
          </div>
          {q.helpText && <p className="text-small text-ink-secondary">{q.helpText}</p>}
        </fieldset>
      );
    }
    case "vial_reference": {
      const ref = value && typeof value === "object" && !Array.isArray(value) ? value : null;
      const schedules = vials?.schedules ?? [];
      const schedule = schedules.find((x) => x.key === ref?.scheduleKey) ?? schedules[0];
      return (
        <div className="flex flex-col gap-3">
          <Select label={`${label}: schedule`} options={schedules.map((x) => ({ value: x.key, label: x.label }))} placeholder="Choose…" value={ref?.scheduleKey ?? ""} onChange={(e) => onChange(e.target.value ? { scheduleKey: e.target.value as "2_per_week" | "3_per_week", vialName: "" } : null)} disabled={disabled || !vials} hint={vials && !vials.unitConfirmed ? "Amounts shown await unit confirmation (Q-12)." : undefined} />
          {ref && schedule && <Select label={`${q.label}: vial`} options={schedule.vials.map((v) => ({ value: v.name, label: `${v.name} · ${v.concentration} · ${v.color}` }))} placeholder="Choose…" value={ref.vialName} onChange={(e) => onChange({ scheduleKey: ref.scheduleKey, vialName: e.target.value })} disabled={disabled} />}
        </div>
      );
    }
    case "text":
    default:
      return (
        <label className="flex flex-col gap-2 text-small font-medium text-ink">
          {label}
          <textarea className={`${control} min-h-20 w-full max-w-[640px]`} value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value || null)} disabled={disabled} rows={2} />
          {q.helpText && <span className="font-normal text-ink-secondary">{q.helpText}</span>}
        </label>
      );
  }
}

/** A whole section's visible questions. */
export function SectionForm({ section, answers, onAnswer, vials, disabled = false }: { section: FormSection; answers: Answers; onAnswer: (key: string, value: AnswerValue | null) => void; vials?: VialProtocols; disabled?: boolean }) {
  return (
    <div className="flex flex-col gap-5">
      {section.questions.filter((q) => visible(q, answers)).map((q) => (
        <QuestionField key={q.key} q={q} value={answers[q.key]} onChange={(v) => onAnswer(q.key, v)} vials={vials} disabled={disabled} />
      ))}
    </div>
  );
}
